import { ApiError, getClientConfig } from "@porchpass/api-client";

type FetchApiResponse<T> = {
  data: T;
  status: number;
  headers: Headers;
};

export async function fetchApi<T>(
  path: string,
  options: RequestInit = {},
): Promise<FetchApiResponse<T>> {
  const config = getClientConfig();
  const token = await config.getAuthToken();
  const method = options.method?.toUpperCase() ?? "GET";
  const isBodyAllowed = !["GET", "HEAD"].includes(method);

  const response = await fetch(`${config.baseUrl}${path}`, {
    ...options,
    method,
    body: isBodyAllowed ? options.body : undefined,
    headers: {
      ...options.headers,
      "x-jwt": token,
      ...(isBodyAllowed ? { "Content-Type": "application/json" } : {}),
    },
  });

  const contentType = response.headers.get("content-type") ?? "";
  const isBinaryResponse =
    contentType.includes("application/pdf") ||
    contentType.includes("application/octet-stream") ||
    contentType.includes("application/zip");

  const data: T | Blob | undefined | Record<string, unknown> =
    response.status === 204
      ? undefined
      : isBinaryResponse
        ? await response.blob()
        : ((await response.json().catch(() => ({}))) as Record<
            string,
            unknown
          >);

  if (!response.ok) {
    throw new ApiError(
      response.status,
      (data as { message?: string })?.message || response.statusText,
      data,
    );
  }

  return {
    data: data as T,
    status: response.status,
    headers: response.headers,
  };
}
